import { useCallback, useEffect, useRef, type FocusEvent } from 'react'
import { usePrepareTurn } from '../../../../hooks/use-prepare-turn'

/**
 * Opening the sidepanel can focus the input and take the focus straight back.
 * Only a focus that is still held after this long counts as reaching for the
 * composer.
 */
const FOCUS_SETTLE_MS = 300

interface UsePrepareTurnOnComposerUseOptions {
  chatId?: string
  /** False while the composer submits somewhere other than a chat turn to `chatId`. */
  enabled: boolean
}

/**
 * Prepares the next turn when the user focuses the composer or types in it.
 * Typing keeps a prepared turn fresh for as long as the message takes to write.
 */
export function usePrepareTurnOnComposerUse({ chatId, enabled }: UsePrepareTurnOnComposerUseOptions) {
  const prepareTurn = usePrepareTurn(chatId)
  const focusTimer = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => () => clearTimeout(focusTimer.current), [])

  const onFocusCapture = useCallback(
    (event: FocusEvent<HTMLElement>) => {
      if (!enabled) {
        return
      }
      const composer = event.currentTarget
      clearTimeout(focusTimer.current)
      focusTimer.current = setTimeout(() => {
        if (composer.contains(document.activeElement)) {
          prepareTurn()
        }
      }, FOCUS_SETTLE_MS)
    },
    [enabled, prepareTurn],
  )

  const onInputCapture = useCallback(() => {
    if (enabled) {
      prepareTurn()
    }
  }, [enabled, prepareTurn])

  return { onFocusCapture, onInputCapture }
}
